import { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { FaBell, FaEnvelope, FaExclamationTriangle, FaSpinner, FaInbox, FaUser, FaClock, FaCheckCircle, FaReply, FaPaperPlane } from 'react-icons/fa';
import UserContext from '../../Components/UserContext';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import { useDateTimeSettings } from '../../hooks/useDateTimeSettings';
import { formatDate as formatDateUtil } from '../../utils/dateFormatter';

function Notifications() {
  const { session } = useContext(UserContext);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [showThread, setShowThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [isPWA, setIsPWA] = useState(false);

  // Tabs for messages vs availability waitlist
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(queryParams.get('tab') || 'messages');
  const [waitlist, setWaitlist] = useState([]);
  const [isLoadingWaitlist, setIsLoadingWaitlist] = useState(false);
  const [waitlistPagination, setWaitlistPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [waitlistFilter, setWaitlistFilter] = useState('pending'); // 'pending' or 'notified'

  // Determine if user is a consultant
  const trainerServices = (session?.services && session.services.length > 0) ? session.services : (session?.service ? [session.service] : []);
  const servicePartnerTypes = session?.servicePartnerTypes || {};
  const activeService = localStorage.getItem('activeService') || trainerServices[0] || '';
  const activePartnerType = activeService ? (servicePartnerTypes[activeService] || 'instructor') : 'instructor';
  const isConsultant = activePartnerType === 'consultant';

  // Fetch notifications/messages from students/clients
  const fetchNotifications = async () => {
    try {
      setError(null);
      setIsLoading(true);
      
      // Check if user is an approved business partner
      if (!session?.isTrainer || session?.trainerStatus !== 'active') {
        setNotifications([]);
        setIsLoading(false);
        return;
      }

      const response = await HttpInterceptor.get('/api/messages/trainer', {
        params: {
          limit: 100,
          page: 1,
        },
      });

      const notificationsData = response.data?.data || response.data || [];
      setNotifications(notificationsData);
      setUnreadCount(response.data?.unreadCount || notificationsData.filter(n => !n.read).length);

      // Mark messages as read when they're viewed (optional - you can make this manual)
      // Uncomment if you want to auto-mark as read when viewing
      // if (notificationsData.length > 0) {
      //   const unreadIds = notificationsData
      //     .filter(n => !n.read)
      //     .map(n => n._id || n.id);
      //   if (unreadIds.length > 0) {
      //     await HttpInterceptor.put('/api/messages/trainer/read', {
      //       messageIds: unreadIds,
      //     });
      //   }
      // }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      const errorMessage = err.response?.data?.message || 'Unable to load notifications';
      setError(errorMessage);
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [session?.isTrainer, session?.trainerStatus]);

  const dateTimeSettings = useDateTimeSettings();
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    // Use user's date format for dates older than a week
    return formatDateUtil(dateString, dateTimeSettings.dateFormat);
  };

  // Mark message as read
  const handleMarkAsRead = async (messageId) => {
    try {
      await HttpInterceptor.put(`/api/messages/trainer/read/${messageId}`);
      // Refresh notifications to update read status
      fetchNotifications();
    } catch (err) {
      console.error('Error marking message as read:', err);
      toast.error('Failed to mark message as read');
    }
  };

  // Load message thread
  const loadThread = async (threadId) => {
    if (showThread === threadId) {
      setShowThread(null);
      setThreadMessages([]);
      return;
    }

    try {
      setIsLoadingThread(true);
      const response = await HttpInterceptor.get(`/api/messages/thread/${threadId}`);
      if (response.status === 200) {
        setThreadMessages(response.data?.data || []);
        setShowThread(threadId);
      }
    } catch (err) {
      console.error('Error loading thread:', err);
      toast.error('Failed to load conversation');
    } finally {
      setIsLoadingThread(false);
    }
  };

  // Send reply
  const handleSendReply = async (messageId) => {
    if (!replyContent.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      setIsSendingReply(true);
      const response = await HttpInterceptor.post(`/api/messages/reply/${messageId}`, {
        content: replyContent
      });

      if (response.status === 201) {
        toast.success('Reply sent successfully');
        setReplyContent('');
        setReplyingTo(null);
        
        // Refresh notifications and thread
        fetchNotifications();
        if (showThread) {
          loadThread(showThread);
        }
      }
    } catch (err) {
      console.error('Error sending reply:', err);
      toast.error('Failed to send reply');
    } finally {
      setIsSendingReply(false);
    }
  };

  // Fetch trainer waitlist (students waiting for availability)
  const fetchWaitlist = async (page = 1) => {
    try {
      setError(null);
      setIsLoadingWaitlist(true);
      
      const response = await HttpInterceptor.get('/api/trainer-availability-notifications/trainer/waitlist', {
        params: {
          isNotified: waitlistFilter === 'notified',
          page,
          limit: 10
        },
      });

      const data = response.data?.data || {};
      setWaitlist(data.notifications || []);
      setWaitlistPagination(data.pagination || { total: 0, page: 1, pages: 1 });
    } catch (err) {
      console.error('Error fetching waitlist:', err);
      const errorMessage = err.response?.data?.message || 'Unable to load waitlist';
      setError(errorMessage);
    } finally {
      setIsLoadingWaitlist(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'messages') {
      fetchNotifications();
    } else {
      fetchWaitlist();
    }
  }, [session?.isTrainer, session?.trainerStatus, activeTab, waitlistFilter]);

  return (
    <div className={`min-h-screen ${isPWA ? 'bg-gradient-to-b from-orange-50/40 via-amber-50/30 to-white' : 'bg-gray-50'} py-8`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`relative ${isPWA ? 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50' : 'bg-white'} ${isPWA ? 'rounded-2xl mb-6 sm:mb-8 overflow-hidden' : 'mb-8 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center p-6 rounded-xl'}`}>
          {isPWA && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
          )}
          <div className={`${isPWA ? 'relative px-4 sm:px-6 lg:px-8 py-8 sm:py-10 pb-12 sm:pb-14' : ''} flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 w-full`}>
            <div>
              <h1 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-3xl'} font-bold ${isPWA ? 'text-orange-800' : 'text-gray-900'} ${isPWA ? 'mb-1' : ''} flex items-center flex-wrap gap-2`}>
                <FaBell className={isPWA ? 'text-orange-600' : 'text-orange-600'} />
                Notifications
                {unreadCount > 0 && (
                  <span className={`ml-3 px-3 py-1 ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md' : 'bg-orange-600 text-white'} text-sm font-semibold rounded-full`}>
                    {unreadCount} unread
                  </span>
                )}
              </h1>
              <p className={`mt-2 ${isPWA ? 'text-sm sm:text-base text-orange-700' : 'text-gray-600'}`}>View messages and notifications from your {isConsultant ? 'clients' : 'students'}</p>
            </div>
            <button
              onClick={activeTab === 'messages' ? fetchNotifications : () => fetchWaitlist(waitlistPagination.page)}
              disabled={isLoading || isLoadingWaitlist}
              className={`px-4 py-2 ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg hover:shadow-xl' : 'bg-orange-600 hover:bg-orange-700 text-white'} rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : ''} disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center`}
              aria-label="Refresh"
            >
              <FaSpinner className={`mr-2 ${(isLoading || isLoadingWaitlist) ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('messages')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'messages' 
              ? isPWA ? 'border-b-2 border-orange-500 text-orange-600' : 'border-b-2 border-orange-600 text-orange-600'
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            Messages
          </button>
          <button
            onClick={() => setActiveTab('waitlist')}
            className={`px-6 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'waitlist' 
              ? isPWA ? 'border-b-2 border-orange-500 text-orange-600' : 'border-b-2 border-orange-600 text-orange-600'
              : 'text-gray-500 hover:text-gray-700'}`}
          >
            Availability Waitlist
            {waitlistPagination.total > 0 && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${activeTab === 'waitlist' ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-500'}`}>
                {waitlistPagination.total}
              </span>
            )}
          </button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm'} p-12 text-center ${isPWA ? 'rounded-2xl' : ''}`}>
            <FaSpinner className={`text-4xl mx-auto ${isPWA ? 'text-orange-600' : 'text-orange-600'} animate-spin mb-4`} />
            <p className={isPWA ? 'text-orange-700 font-medium' : 'text-gray-600'}>Loading notifications...</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className={`${isPWA ? 'bg-gradient-to-br from-white to-red-50/30 border-2 border-red-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm'} p-12 text-center ${isPWA ? 'rounded-2xl' : ''}`}>
            <FaExclamationTriangle className={`text-4xl mx-auto ${isPWA ? 'text-red-500' : 'text-gray-400'} mb-4`} />
            <p className={`${isPWA ? 'text-red-700' : 'text-gray-600'} mb-4`}>{error}</p>
            <button
              onClick={fetchNotifications}
              className={`px-4 py-2 ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg hover:shadow-xl' : 'bg-orange-600 hover:bg-orange-700 text-white'} rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : ''}`}
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty State (Messages Tab) */}
        {!error && !isLoading && activeTab === 'messages' && notifications.length === 0 && (
          <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/30 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm'} p-12 text-center ${isPWA ? 'rounded-2xl' : ''}`}>
            <FaInbox className={`text-6xl mx-auto ${isPWA ? 'text-orange-300' : 'text-gray-400'} mb-4 ${isPWA ? 'opacity-60' : ''}`} />
            <h3 className={`text-xl font-semibold ${isPWA ? 'text-orange-800' : 'text-gray-900'} mb-2`}>No Notifications</h3>
            <p className={`${isPWA ? 'text-orange-700' : 'text-gray-600'} mb-6`}>You don't have any messages or notifications from {isConsultant ? 'clients' : 'students'} yet.</p>
            <p className={`text-sm ${isPWA ? 'text-orange-600' : 'text-gray-500'}`}>When {isConsultant ? 'clients' : 'students'} send you messages, they will appear here.</p>
          </div>
        )}

        {/* Notifications List (Messages Tab) */}
        {!error && !isLoading && activeTab === 'messages' && notifications.length > 0 && (
          <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm'} overflow-hidden ${isPWA ? 'rounded-2xl' : ''}`}>
            <div className={`divide-y ${isPWA ? 'divide-orange-200/50' : 'divide-gray-200'}`}>
              {notifications.map((notification) => {
                const threadId = notification.threadId || notification._id || notification.id;
                const isThreadOpen = showThread === threadId;
                
                return (
                  <div key={notification._id || notification.id}>
                    <div
                      className={`p-6 ${isPWA ? 'hover:bg-orange-50/50' : 'hover:bg-gray-50'} transition-colors ${
                        !notification.read ? isPWA ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-500' : 'bg-indigo-50 border-l-4 border-indigo-500' : ''
                      }`}
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className={`h-10 w-10 rounded-full ${isPWA ? 'bg-gradient-to-br from-orange-100 to-amber-100' : 'bg-indigo-100'} flex items-center justify-center overflow-hidden`}>
                            {notification.studentImage ? (
                              <img
                                src={notification.studentImage}
                                alt={notification.studentName}
                                className="h-10 w-10 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className={`h-10 w-10 rounded-full ${isPWA ? 'bg-gradient-to-br from-orange-100 to-amber-100' : 'bg-indigo-100'} flex items-center justify-center ${notification.studentImage ? 'hidden' : ''}`}>
                              <FaUser className={isPWA ? 'text-orange-600' : 'text-orange-600'} />
                            </div>
                          </div>
                        </div>
                        <div className="ml-4 flex-1">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-900">
                                  {notification.studentName || (isConsultant ? 'Client' : 'Student')}
                                </p>
                                {notification.programName && (
                                  <span className="text-xs text-gray-500">• {notification.programName}</span>
                                )}
                                {!notification.read && (
                                  <span className={`h-2 w-2 ${isPWA ? 'bg-orange-600' : 'bg-orange-600'} rounded-full`}></span>
                                )}
                              </div>
                              <p className={`text-sm ${isPWA ? 'text-orange-700' : 'text-gray-700'} mt-1 whitespace-pre-wrap`}>
                                {notification.message || notification.content || 'New message'}
                              </p>
                              <div className="flex items-center gap-3 mt-2">
                                {notification.type && (
                                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${isPWA ? 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 shadow-sm' : 'bg-indigo-100 text-indigo-800'}`}>
                                    {notification.type}
                                  </span>
                                )}
                                <p className="text-xs text-gray-500 flex items-center">
                                  <FaClock className="mr-1" />
                                  {formatDate(notification.createdAt || notification.date)}
                                </p>
                              </div>
                            </div>
                            <div className="ml-4 flex-shrink-0 flex items-center gap-2">
                              {!notification.read && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsRead(notification._id || notification.id);
                                  }}
                                  className={`p-2 ${isPWA ? 'text-orange-600 hover:bg-orange-100' : 'text-orange-600 hover:bg-indigo-100'} rounded-full transition-colors`}
                                  aria-label="Mark as read"
                                  title="Mark as read"
                                >
                                  <FaCheckCircle className="text-sm" />
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (replyingTo === notification._id || replyingTo === notification.id) {
                                    setReplyingTo(null);
                                  } else {
                                    setReplyingTo(notification._id || notification.id);
                                  }
                                }}
                                className={`p-2 ${isPWA ? 'text-orange-600 hover:bg-orange-100' : 'text-blue-600 hover:bg-blue-100'} rounded-full transition-colors`}
                                aria-label="Reply"
                                  title="Reply"
                                >
                                  <FaReply className="text-sm" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    loadThread(threadId);
                                  }}
                                  className={`p-2 ${isPWA ? 'text-orange-600 hover:bg-orange-100' : 'text-gray-600 hover:bg-gray-100'} rounded-full transition-colors text-xs`}
                                  aria-label="View conversation"
                                  title="View conversation"
                                >
                                  {isThreadOpen ? 'Hide' : 'Thread'}
                                </button>
                            </div>
                          </div>

                          {/* Reply Input */}
                          {replyingTo === (notification._id || notification.id) && (
                            <div className={`mt-4 pt-4 border-t ${isPWA ? 'border-orange-200/50' : 'border-gray-200'}`}>
                              <textarea
                                value={replyContent}
                                onChange={(e) => setReplyContent(e.target.value)}
                                placeholder="Type your reply..."
                                className={`w-full px-3 py-2 border ${isPWA ? 'border-orange-200 focus:border-orange-400' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 ${isPWA ? 'focus:ring-orange-400' : 'focus:ring-indigo-500'} resize-none ${isPWA ? 'bg-white/80' : 'bg-white'}`}
                                rows="3"
                              />
                                <div className="flex justify-end gap-2 mt-2">
                                  <button
                                    onClick={() => {
                                      setReplyingTo(null);
                                      setReplyContent('');
                                    }}
                                    className={`px-4 py-2 ${isPWA ? 'text-orange-700 border border-orange-200 hover:bg-orange-50' : 'text-gray-700 border border-gray-300 hover:bg-gray-50'} rounded-lg transition-colors ${isPWA ? 'font-medium' : ''}`}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => handleSendReply(notification._id || notification.id)}
                                    disabled={isSendingReply || !replyContent.trim()}
                                    className={`px-4 py-2 ${isPWA ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg' : 'bg-orange-600 hover:bg-orange-700 text-white'} rounded-lg transition-all duration-200 ${isPWA ? 'font-medium' : ''} disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center`}
                                  >
                                    {isSendingReply ? (
                                      <>
                                        <FaSpinner className="mr-2 animate-spin" />
                                        Sending...
                                      </>
                                    ) : (
                                      <>
                                        <FaPaperPlane className="mr-2" />
                                        Send Reply
                                      </>
                                    )}
                                  </button>
                                </div>
                            </div>
                          )}

                          {/* Thread Messages */}
                          {isThreadOpen && (
                            <div className={`mt-4 pt-4 border-t ${isPWA ? 'border-orange-200/50' : 'border-gray-200'}`}>
                              {isLoadingThread ? (
                                <div className="flex justify-center py-4">
                                  <FaSpinner className={`animate-spin ${isPWA ? 'text-orange-600' : 'text-orange-600'}`} />
                                </div>
                              ) : threadMessages.length > 0 ? (
                                <div className="space-y-3 max-h-64 overflow-y-auto">
                                  {threadMessages.map((msg) => (
                                    <div
                                      key={msg._id || msg.id}
                                      className={`p-3 rounded-lg ${
                                        msg.isReply
                                          ? isPWA ? 'bg-gradient-to-r from-orange-50 to-amber-50 ml-8 border border-orange-200/50' : 'bg-blue-50 ml-8'
                                          : isPWA ? 'bg-orange-50/50 border border-orange-200/30' : 'bg-gray-50'
                                      }`}
                                    >
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-medium text-gray-700">
                                          {msg.isReply ? 'You' : msg.studentName || (isConsultant ? 'Client' : 'Student')}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {formatDate(msg.createdAt || msg.date)}
                                        </span>
                                      </div>
                                      <p className="text-sm text-gray-700">{msg.content || msg.message}</p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500 text-center py-2">No conversation history</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Waitlist Filter Toggle */}
        {activeTab === 'waitlist' && (
          <div className="flex bg-white p-1 rounded-lg border border-gray-200 w-fit mb-6 shadow-sm">
            <button
              onClick={() => setWaitlistFilter('pending')}
              className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${
                waitlistFilter === 'pending'
                  ? 'bg-orange-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Pending Notification
            </button>
            <button
              onClick={() => setWaitlistFilter('notified')}
              className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${
                waitlistFilter === 'notified'
                  ? 'bg-orange-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Already Notified
            </button>
          </div>
        )}

        {/* Waitlist List (Availability Waitlist Tab) */}
        {!error && !isLoadingWaitlist && activeTab === 'waitlist' && waitlist.length > 0 && (
          <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 border-2 border-orange-200/50 shadow-xl' : 'bg-white rounded-xl shadow-sm'} overflow-hidden ${isPWA ? 'rounded-2xl' : ''}`}>
            <div className={`divide-y ${isPWA ? 'divide-orange-200/50' : 'divide-gray-200'}`}>
              {waitlist.map((request) => (
                <div key={request._id || request.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className={`h-10 w-10 rounded-full ${isPWA ? 'bg-gradient-to-br from-orange-100 to-amber-100' : 'bg-indigo-100'} flex items-center justify-center`}>
                        <FaUser className={isPWA ? 'text-orange-600' : 'text-orange-600'} />
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {request.userId?.name || 'Anonymous User'}
                          </p>
                          <p className="text-xs text-gray-500">{request.email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500 flex items-center justify-end">
                            <FaClock className="mr-1" />
                            Requested {formatDate(request.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2">
                        <p className="text-sm text-gray-700">
                          Waiting for: <span className="font-semibold text-orange-600">
                            {request.programId?.name || 'Any Program'}
                          </span>
                        </p>
                        {request.programId?.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1">{request.programId.description}</p>
                        )}
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full ${isPWA ? 'bg-orange-100 text-orange-700' : 'bg-indigo-100 text-indigo-700'}`}>
                          {request.programId ? 'Program Level' : 'Trainer Level'}
                        </span>
                        {request.isNotified ? (
                          <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                            <FaCheckCircle className="text-[10px]" />
                            Notified on {formatDateUtil(request.notifiedAt)}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-blue-100 text-blue-700">
                            Pending Availability
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Waitlist Pagination */}
            {waitlistPagination.pages > 1 && (
              <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-center gap-2">
                {Array.from({ length: waitlistPagination.pages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => fetchWaitlist(p)}
                    className={`px-3 py-1 text-xs rounded-md ${waitlistPagination.page === p ? 'bg-orange-600 text-white' : 'bg-white text-gray-600 border border-gray-300'}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Waitlist Loading State */}
        {isLoadingWaitlist && activeTab === 'waitlist' && (
          <div className="p-12 text-center bg-white rounded-xl shadow-sm">
            <FaSpinner className="text-4xl mx-auto text-orange-600 animate-spin mb-4" />
            <p className="text-gray-600">Loading waitlist...</p>
          </div>
        )}

        {/* Empty Waitlist State */}
        {!error && !isLoadingWaitlist && activeTab === 'waitlist' && waitlist.length === 0 && (
          <div className="p-12 text-center bg-white rounded-xl shadow-sm">
            <FaInbox className="text-6xl mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Waitlist is Empty</h3>
            <p className="text-gray-600">
              {waitlistFilter === 'pending' 
                ? 'No students are currently waiting for your availability.' 
                : 'No notification history found.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Notifications;

